<template>
    <SnackbarComponent v-model="snackbar.show" :message="snackbar.message" :color="snackbar.color" />
    <CreateUserGuideDialog v-model="showCreateDialog" @created="handleUserGuideCreated" />
    <EditUserGuideDialog v-model="showEditDialog" :UserGuide="selectedUserGuide" @updated="handleChecklistUpdated" />
    <CreateNestUserGuideDialog v-model="showNestedGuidDialog" :UserGuideCreate="selectedUserGuide"
        :selectedSubGuide="selectedSubGuide" @created="handleChecklistUpdated" />
    <DeleteUserGuideDialog v-model="showDeleteDialog" :UserGuide="selectedUserGuide"
        @deleted="handleChecklistUpdated" />
    <CreateSubtitleGuideDialog v-model="showCreateSubtitleDialog" :UserGuide="selectedUserGuide"
        @updated="handleChecklistUpdated" />
    <EditNestUserGuideDialog v-model="showEditNestedGuidDialog" :UserGuideCreate="selectedUserGuide"
        :selectedSubGuide="selectedSubGuide" @created="handleChecklistUpdated" />
    <!-- Page Loader -->
    <v-overlay :model-value="loading" persistent class="align-center justify-center">
        <v-progress-circular indeterminate size="60" width="5" color="primary" />
    </v-overlay>
    <div>
        <div class="d-flex justify-space-between align-center pa-4">
            <span class="text-h5">User Guide </span>
            <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">Add
                User Guide</v-btn>
        </div>
        <v-expansion-panels variant="inset">
            <v-expansion-panel v-for="guide in guides" :key="guide.main_module_id"
                @group:selected="({ value }) => value && loadSubGuides(guide)">
                <v-expansion-panel-title>
                    <div class="w-100 d-flex justify-space-between align-center">

                        <div>
                            <div class="font-weight-bold">
                                {{ guide.title }}
                            </div>

                            <div class="text-caption">
                                {{ guide.guide_topic_status }}
                            </div>
                        </div>

                        <div class="d-flex align-center">
                            <v-tooltip text="Add Subtitle" location="top">
                                <template #activator="{ props }">
                                    <span v-bind="props">
                                        <v-btn icon="mdi-folder-plus-outline" color="primary" variant="text"
                                            size="small" @click.stop="openCreateSubtitleDialog(guide)" />
                                    </span>
                                </template>
                            </v-tooltip>

                            <v-tooltip text="Edit Guide" location="top">
                                <template #activator="{ props }">
                                    <span v-bind="props">
                                        <v-btn icon="mdi-pencil" color="primary" variant="text" size="small"
                                            @click.stop="openEditDialog(guide)" />
                                    </span>
                                </template>
                            </v-tooltip>

                            <v-tooltip text="Change Status" location="top">
                                <template #activator="{ props }">
                                    <span v-bind="props">
                                        <v-btn icon="mdi-list-status" color="primary" variant="text" size="small"
                                            @click.stop="changeStatus(guide)" />
                                    </span>
                                </template>
                            </v-tooltip>

                            <v-tooltip text="Delete Guide" location="top">
                                <template #activator="{ props }">
                                    <span v-bind="props">
                                        <v-btn icon="mdi-delete" color="error" variant="text" size="small"
                                            @click.stop="openDeleteDialog(guide)" />
                                    </span>
                                </template>
                            </v-tooltip>
                        </div>

                    </div>
                </v-expansion-panel-title>

                <v-expansion-panel-text>

                    <v-list v-if="guide.guide_sub_modules.length" density="comfortable">
                        <v-list-item v-for="item in guide.guide_sub_modules" :key="item.guide_topic_id">
                            <div class="d-flex justify-space-between align-center w-100">
                                <div>
                                    <div class="font-weight-medium">
                                        {{ item.title }}
                                    </div>

                                    <div class="text-caption text-grey">
                                        {{ item.guide_topic_status }}
                                    </div>
                                </div>

                                <div class="d-flex align-center">
                                    <!-- Show Add only if no S3 content exists -->
                                    <v-btn v-if="!item.sub_module_content_s3_path" icon="mdi-plus" color="primary"
                                        variant="text" size="small" @click.stop="createItem(guide, item)" />

                                    <!-- Show Edit only if S3 content exists -->
                                    <v-btn v-else icon="mdi-pencil" color="primary" variant="text" size="small"
                                        @click.stop="editItem(guide, item)" />

                                    <v-btn icon="mdi-list-status" color="primary" variant="text" size="small"
                                        @click.stop="changeNestStatus(guide, item)" />

                                    <v-btn icon="mdi-delete" color="error" variant="text" size="small"
                                        @click.stop="DeleteNestGudies(guide, item)" />
                                </div>
                            </div>
                        </v-list-item>
                    </v-list>

                    <v-alert v-else type="info" variant="tonal">
                        No sub guides found.
                    </v-alert>

                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import EditNestUserGuideDialog from "@/components/UserGuide/Dialogs/EditNestUserGuideDialog.vue";
import CreateSubtitleGuideDialog from "@/components/UserGuide/Dialogs/CreateSubtitleGuideDialog.vue";
import GetUserSubGuideList from "@/mixins/UserGuide/GetUserSubGuideList";
import DeleteUserGuideDialog from "@/components/UserGuide/Dialogs/DeleteUserGuideDialog.vue";
import CreateNestUserGuideDialog from "@/components/UserGuide/Dialogs/CreateNestUserGuideDialog.vue";
import EditUserGuideDialog from "@/components/UserGuide/Dialogs/EditUserGuideDialog.vue";
import CreateUserGuideDialog from "@/components/UserGuide/Dialogs/CreateUserGuideDialog.vue";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import GetUserGuideLists from '@/mixins/UserGuide/GetUserGuideLists';
import { edit_user_guide_topic } from '@/graphql/mutations.js';
import { generateClient } from "aws-amplify/api";
const client = generateClient();

export default {
    mixins: [GetUserGuideLists, GetUserSubGuideList],
    components: {
        SnackbarComponent,
        CreateUserGuideDialog,
        EditUserGuideDialog,
        CreateNestUserGuideDialog,
        DeleteUserGuideDialog,
        CreateSubtitleGuideDialog,
        EditNestUserGuideDialog
    },
    data() {
        return {
            loading: false,
            openedPanel: null,
            snackbar: {
                show: false,
                message: "",
                color: "success",
            },
            selectedSubGuide: null,
            showDeleteDialog: false,
            showNestedGuidDialog: false,
            showEditNestedGuidDialog: false,
            showCreateSubtitleDialog: false,
            showEditDialog: false,
            showCreateDialog: false,
            selectedUserGuide: null,
            guides: [],
        };
    },

    async mounted() {
        this.loading = true;

        try {
            await this.fetchUserGuideMainList();

            this.guides = this.UserGuideLists.map(item => ({
                ...item,
                guide_sub_modules: [],
                loaded: false,
            }));
        } finally {
            this.loading = false;
        }
    },
    methods: {
        async changeStatus(guide) {
            this.loading = true;

            try {
                const newStatus =
                    guide.guide_topic_status === "PUBLISHED"
                        ? "UNPUBLISHED"
                        : "PUBLISHED";

                const input = {
                    guide_topic_id: guide.guide_topic_id,
                    guide_topic_status: newStatus,
                };

                const response = await client.graphql({
                    query: edit_user_guide_topic,
                    variables: {
                        input,
                    },
                });

                const responseData = JSON.parse(response.data.edit_user_guide_topic);

                if (responseData.status === "SUCCESS") {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            `User Guide ${newStatus.toLowerCase()} successfully!`,
                        color: "success",
                    };
                } else {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            `Failed to ${newStatus.toLowerCase()} User Guide`,
                        color: "error",
                    };
                }
            } catch (error) {
                console.error(`Error changing User Guide status:`, error);

                this.snackbar = {
                    show: true,
                    message:
                        error.errors?.[0]?.message ||
                        error.message ||
                        "An error occurred while updating User Guide status",
                    color: "error",
                };
            } finally {
                this.loading = false;
                await this.fetchUserGuideMainList();

                this.guides = this.UserGuideLists.map(item => ({
                    ...item,
                    guide_sub_modules: [],
                    loaded: false,
                }));
            }
        },
        async changeNestStatus(guide, selectedSubGuide) {
            this.loading = true;

            try {
                const newStatus =
                    selectedSubGuide.guide_topic_status === "PUBLISHED"
                        ? "UNPUBLISHED"
                        : "PUBLISHED";

                const input = {
                    guide_topic_id: selectedSubGuide.guide_topic_id,
                    guide_topic_status: newStatus,
                };

                const response = await client.graphql({
                    query: edit_user_guide_topic,
                    variables: {
                        input,
                    },
                });

                const responseData = JSON.parse(response.data.edit_user_guide_topic);

                if (responseData.status === "SUCCESS") {
                    // Update the status immediately in the UI
                    selectedSubGuide.guide_topic_status = newStatus;

                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            `User Guide ${newStatus.toLowerCase()} successfully!`,
                        color: "success",
                    };

                    // Reload the sub guides for this main guide
                    guide.loaded = false;
                    await this.loadSubGuides(guide);
                } else {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            `Failed to ${newStatus.toLowerCase()} User Guide`,
                        color: "error",
                    };
                }
            } catch (error) {
                console.error("Error changing User Guide status:", error);

                this.snackbar = {
                    show: true,
                    message:
                        error.errors?.[0]?.message ||
                        error.message ||
                        "An error occurred while updating User Guide status",
                    color: "error",
                };
            } finally {
                this.loading = false;
            }
        },
        async DeleteNestGudies(guide, selectedSubGuide) {
            this.loading = true;

            try {
                const input = {
                    guide_topic_id: selectedSubGuide.guide_topic_id,
                    guide_topic_status: "DEACTIVE",
                };

                const response = await client.graphql({
                    query: edit_user_guide_topic,
                    variables: {
                        input,
                    },
                });

                const responseData = JSON.parse(response.data.edit_user_guide_topic);

                if (responseData.status === "SUCCESS") {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "User Guide deleted successfully!",
                        color: "success",
                    };

                    // Reload only this guide's sub guides
                    guide.loaded = false;
                    await this.loadSubGuides(guide);
                } else {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "Failed to delete User Guide",
                        color: "error",
                    };
                }
            } catch (error) {
                console.error("Error deleting User Guide:", error);

                this.snackbar = {
                    show: true,
                    message:
                        error.errors?.[0]?.message ||
                        error.message ||
                        "An error occurred while deleting User Guide",
                    color: "error",
                };
            } finally {
                this.loading = false;
            }
        },
        async loadSubGuides(guide) {
            if (guide.loaded) return;

            this.loading = true;

            try {
                await this.fetchUserSubGuideMainList(guide.main_module_id);

                guide.guide_sub_modules = [...this.UserGuideLists];
                guide.loaded = true;
            } finally {
                this.loading = false;
            }
        },
        async handleChecklistUpdated() {
            this.loading = true;

            try {
                await this.fetchUserGuideMainList();

                this.guides = this.UserGuideLists.map(item => ({
                    ...item,
                    guide_sub_modules: [],
                    loaded: false,
                }));
            } finally {
                this.loading = false;
            }
        },
        async handleUserGuideCreated() {
            this.loading = true;

            try {
                await this.fetchUserGuideMainList();

                this.guides = this.UserGuideLists.map(item => ({
                    ...item,
                    guide_sub_modules: [],
                    loaded: false,
                }));
            } finally {
                this.loading = false;
            }
        },
        openCreateDialog() {
            this.showCreateDialog = true;
        },
        openCreateSubtitleDialog(guide) {
            this.selectedUserGuide = JSON.parse(JSON.stringify(guide));
            this.showCreateSubtitleDialog = true;
        },
        openEditDialog(guide) {
            this.selectedUserGuide = JSON.parse(JSON.stringify(guide));
            this.showEditDialog = true;
        },

        openDeleteDialog(guide) {
            this.selectedUserGuide = JSON.parse(JSON.stringify(guide));
            this.showDeleteDialog = true;
        },
        createItem(guide, subGuide) {
            this.selectedUserGuide = JSON.parse(JSON.stringify(guide));
            this.selectedSubGuide = JSON.parse(JSON.stringify(subGuide));

            this.showNestedGuidDialog = true;
        },
        editItem(guide, subGuide) {
            console.log("Edit clicked", guide, subGuide);

            this.selectedUserGuide = JSON.parse(JSON.stringify(guide));
            this.selectedSubGuide = JSON.parse(JSON.stringify(subGuide));

            this.showEditNestedGuidDialog = true;
        },
        CreateMainNode() {
            try {

            } catch (error) {
                console.log("error", error);
            }
        },
        // async CreateMainNode() {
        //     this.loading = true;

        //     try {
        //         const input = {
        //             announcement_message: this.formData.announcement_message,
        //             announcement_source: "BMS",
        //             announcement_status: "PUBLISH",
        //             announcement_visiblity: this.formData.announcement_visiblity,
        //         };

        //         // Add visible_for only if visibility is ORGANIZATION
        //         if (this.formData.announcement_visiblity === "ORGANIZATION") {
        //             input.visible_for = this.formData.visible_for;

        //             // Optionally add visible_for_name (organization names)
        //             const selectedOptions = this.organizationOptions.filter(
        //                 (opt) => this.formData.visible_for.includes(opt.value)
        //             );
        //             input.visible_for_name = selectedOptions.map((opt) => opt.title);
        //         }

        //         const response = await client.graphql({
        //             query: create_announcement,
        //             variables: {
        //                 input: input,
        //             },
        //         });

        //         const responseData = JSON.parse(response.data.create_announcement);

        //         if (responseData.status === "SUCCESS") {
        //             this.snackbar = {
        //                 show: true,
        //                 message: responseData.status_message || "Announcement created successfully!",
        //                 color: "success",
        //             };
        //             this.$emit("created");
        //             this.handleClose();
        //         } else {
        //             this.snackbar = {
        //                 show: true,
        //                 message: responseData.status_message || "Failed to create announcement",
        //                 color: "error",
        //             };
        //         }
        //     } catch (error) {
        //         console.error("Error creating announcement:", error);
        //         this.snackbar = {
        //             show: true,
        //             message:
        //                 (error.errors && error.errors[0]?.message) ||
        //                 error.message ||
        //                 "An error occurred while creating announcement",
        //             color: "error",
        //         };
        //     } finally {
        //         this.loading = false;
        //     }
        // },
    }
};
</script>

<style scoped>
.w-100 {
    width: 100%;
}
</style>